import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  SelectChangeEvent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useAuth } from '../context/AuthContext';
import productsService, {
  type EvaporationDefaultDetail,
  type EvaporationDefaultLimit,
  type EvaporationDefaultLimitInput,
} from '../services/products';

type Mode = 'view' | 'edit' | 'create';

// Limit type codes mirror the legacy LimitsType values shared with Mass
// Concrete Defaults Limits (01-04). For evaporation these gate the
// measured evaporation rate rather than temperature.
const LIMIT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '01', label: 'Greater Than' },
  { value: '02', label: 'Greater than and equal to' },
  { value: '03', label: 'Less than' },
  { value: '04', label: 'Less than and equal to' },
];

const labelFromOptions = (
  options: { value: string; label: string }[],
  value: string,
): string => options.find((o) => o.value === value)?.label || value;

interface DefaultFormState {
  default_id: string;
  description: string;
  rev_no: string;
}

const EMPTY_DEFAULT: DefaultFormState = {
  default_id: '',
  description: '',
  rev_no: '1',
};

interface LimitFormState {
  purpose: string;
  limits_type: string;
  limits_rate: string;
  background_color: string;
  text_color: string;
}

const EMPTY_LIMIT: LimitFormState = {
  purpose: '',
  limits_type: '01',
  limits_rate: '',
  background_color: '#ffffff',
  text_color: '#000000',
};

const limitToForm = (l: EvaporationDefaultLimit): LimitFormState => ({
  purpose: l.purpose || '',
  limits_type: l.limits_type || '01',
  limits_rate:
    l.limits_rate !== undefined && l.limits_rate !== null ? String(l.limits_rate) : '',
  background_color: l.background_color || '#ffffff',
  text_color: l.text_color || '#000000',
});

const formToLimitPayload = (f: LimitFormState): EvaporationDefaultLimitInput => ({
  purpose: f.purpose.trim(),
  limits_type: f.limits_type,
  limits_rate: Number(f.limits_rate),
  background_color: f.background_color || undefined,
  text_color: f.text_color || undefined,
});

const EvaporationLimitForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const mode: Mode = id == null
    ? 'create'
    : location.pathname.endsWith('/edit')
      ? 'edit'
      : 'view';

  const isView = mode === 'view';
  const isCreate = mode === 'create';
  const canManage = user?.UserType === '01';

  // Parent default
  const [defaultForm, setDefaultForm] = useState<DefaultFormState>(EMPTY_DEFAULT);
  const [detail, setDetail] = useState<EvaporationDefaultDetail | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Child limits
  const [limits, setLimits] = useState<EvaporationDefaultLimit[]>([]);
  const [limitDialog, setLimitDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    editingId: string | null;
    form: LimitFormState;
  }>({ open: false, mode: 'create', editingId: null, form: EMPTY_LIMIT });
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitDeleteTarget, setLimitDeleteTarget] = useState<EvaporationDefaultLimit | null>(null);
  const [limitDeleting, setLimitDeleting] = useState(false);

  // Initial load
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await productsService.getEvaporationDefault(id);
        if (cancelled) return;
        setDetail(d);
        setDefaultForm({
          default_id: d.default_id || '',
          description: d.description || '',
          rev_no: d.rev_no || '1',
        });
        setLimits(d.limits || []);
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Refresh just the limits — used after add/edit/delete.
  const reloadLimits = useCallback(async () => {
    if (!id) return;
    try {
      const fresh = await productsService.listEvaporationLimits(id);
      setLimits(fresh);
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || 'Failed to refresh limits',
        severity: 'error',
      });
    }
  }, [id]);

  const handleField = (field: keyof DefaultFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDefaultForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const validateDefault = (): string | null => {
    if (!defaultForm.default_id.trim()) {
      return t('evaporationLimit.defaultIdRequired', 'Group ID is required');
    }
    if (!defaultForm.description.trim()) {
      return t('evaporationLimit.descriptionRequired', 'Description is required');
    }
    if (!defaultForm.rev_no.trim()) {
      return t('evaporationLimit.revNoRequired', 'Rev No is required');
    }
    return null;
  };

  const handleSaveDefault = async () => {
    const err = validateDefault();
    if (err) {
      setSnackbar({ msg: err, severity: 'error' });
      return;
    }
    if (!user?.CompanyID) {
      setSnackbar({ msg: 'Missing company context', severity: 'error' });
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isCreate) {
        const created = await productsService.createEvaporationDefault({
          company_id: user.CompanyID,
          default_id: defaultForm.default_id.trim(),
          description: defaultForm.description.trim(),
          rev_no: defaultForm.rev_no.trim(),
        });
        setSnackbar({
          msg: t('evaporationLimit.created', 'Evaporation limit group created'),
          severity: 'success',
        });
        navigate(`/evaporation-limits/${created.id}`, { replace: true });
      } else if (id) {
        await productsService.updateEvaporationDefault(id, {
          default_id: defaultForm.default_id.trim(),
          description: defaultForm.description.trim(),
          rev_no: defaultForm.rev_no.trim(),
        });
        setSnackbar({
          msg: t('evaporationLimit.updated', 'Evaporation limit group updated'),
          severity: 'success',
        });
        navigate(`/evaporation-limits/${id}`, { replace: true });
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Limit dialog handlers
  const openCreateLimit = () => {
    setLimitDialog({ open: true, mode: 'create', editingId: null, form: EMPTY_LIMIT });
  };
  const openEditLimit = (limit: EvaporationDefaultLimit) => {
    setLimitDialog({
      open: true,
      mode: 'edit',
      editingId: limit.id,
      form: limitToForm(limit),
    });
  };
  const closeLimitDialog = () => {
    if (limitSaving) return;
    setLimitDialog((prev) => ({ ...prev, open: false }));
  };
  const setLimitField = (field: keyof LimitFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setLimitDialog((prev) => ({
        ...prev,
        form: { ...prev.form, [field]: e.target.value },
      }));
    };
  const setLimitSelect = (field: keyof LimitFormState) => (e: SelectChangeEvent) => {
    setLimitDialog((prev) => ({
      ...prev,
      form: { ...prev.form, [field]: e.target.value as string },
    }));
  };

  const validateLimit = (f: LimitFormState): string | null => {
    if (!f.purpose.trim()) return t('evaporationLimit.purposeRequired', 'Purpose is required');
    if (!f.limits_type) return t('evaporationLimit.limitTypeRequired', 'Limit type is required');
    if (f.limits_rate === '' || Number.isNaN(Number(f.limits_rate))) {
      return t('evaporationLimit.rateRequired', 'Limit rate is required');
    }
    return null;
  };

  const handleSaveLimit = async () => {
    if (!id) return;
    const err = validateLimit(limitDialog.form);
    if (err) {
      setSnackbar({ msg: err, severity: 'error' });
      return;
    }
    setLimitSaving(true);
    try {
      const payload = formToLimitPayload(limitDialog.form);
      if (limitDialog.mode === 'create') {
        await productsService.createEvaporationLimit(id, payload);
        setSnackbar({ msg: t('evaporationLimit.limitCreated', 'Limit added'), severity: 'success' });
      } else if (limitDialog.editingId) {
        await productsService.updateEvaporationLimit(id, limitDialog.editingId, payload);
        setSnackbar({ msg: t('evaporationLimit.limitUpdated', 'Limit updated'), severity: 'success' });
      }
      setLimitDialog({ open: false, mode: 'create', editingId: null, form: EMPTY_LIMIT });
      await reloadLimits();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || 'Save failed',
        severity: 'error',
      });
    } finally {
      setLimitSaving(false);
    }
  };

  const handleDeleteLimit = async () => {
    if (!id || !limitDeleteTarget) return;
    setLimitDeleting(true);
    try {
      await productsService.deleteEvaporationLimit(id, limitDeleteTarget.id);
      setSnackbar({ msg: t('evaporationLimit.limitDeleted', 'Limit deleted'), severity: 'success' });
      setLimitDeleteTarget(null);
      await reloadLimits();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || 'Delete failed',
        severity: 'error',
      });
    } finally {
      setLimitDeleting(false);
    }
  };

  const limitColumns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions', 'Actions'),
      width: 110,
      getActions: (params: GridRowParams) => {
        if (isView || !canManage) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={t('common.edit', 'Edit')}
            onClick={() => openEditLimit(params.row)}
            color="primary"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon sx={{ color: 'error.main' }} />}
            label={t('common.delete', 'Delete')}
            onClick={() => setLimitDeleteTarget(params.row)}
          />,
        ];
      },
    },
    {
      field: 'purpose',
      headerName: t('evaporationLimit.purpose', 'Purpose'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'limits_type',
      headerName: t('evaporationLimit.sign', 'Sign'),
      flex: 1,
      minWidth: 180,
      valueFormatter: (value: string) => labelFromOptions(LIMIT_TYPE_OPTIONS, value),
    },
    {
      field: 'limits_rate',
      headerName: t('evaporationLimit.rate', 'Rate (kg/m²/h)'),
      flex: 0.8,
      minWidth: 140,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        const bg = params.row.background_color || 'transparent';
        const fg = params.row.text_color || 'inherit';
        return (
          <Box
            sx={{
              backgroundColor: bg,
              color: fg,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              minWidth: 50,
              textAlign: 'center',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
  ];

  const titleByMode = useCallback(() => {
    if (isCreate) return t('evaporationLimit.addTitle', 'Add Evaporation Limit Group');
    if (isView) return t('evaporationLimit.viewTitle', 'View Evaporation Limit Group');
    return t('evaporationLimit.editTitle', 'Edit Evaporation Limit Group');
  }, [isCreate, isView, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/evaporation-limits')}>
          {t('evaporationLimit.backToList', 'Back to List')}
        </Button>
        {isView && id && canManage && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/evaporation-limits/${id}/edit`)}
          >
            {t('common.edit', 'Edit')}
          </Button>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>{titleByMode()}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('evaporationLimit.defaultId', 'Group ID')}
              value={defaultForm.default_id}
              onChange={handleField('default_id')}
              disabled={isView}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('evaporationLimit.revNo', 'Rev No')}
              value={defaultForm.rev_no}
              onChange={handleField('rev_no')}
              disabled={isView}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('evaporationLimit.description', 'Description')}
              value={defaultForm.description}
              onChange={handleField('description')}
              disabled={isView}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {!isCreate && detail && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('evaporationLimit.createdBy', 'Created By')}
                  value={detail.create_by || ''}
                  disabled
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('evaporationLimit.modifiedBy', 'Modified By')}
                  value={detail.modified_by || ''}
                  disabled
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {!isView && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => navigate('/evaporation-limits')} disabled={saving}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveDefault}
              disabled={saving}
            >
              {saving
                ? t('common.saving', 'Saving...')
                : isCreate
                  ? t('common.create', 'Create')
                  : t('common.save', 'Save')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Child limits — only visible after the parent default exists */}
      {!isCreate && id && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {t('evaporationLimit.limitsSection', 'Limits')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'evaporationLimit.limitsHint',
                  'Evaporation rate thresholds applied to pours using this group.',
                )}
              </Typography>
            </Box>
            {!isView && canManage && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                onClick={openCreateLimit}
                sx={{ textTransform: 'none', px: 3, py: 1 }}
              >
                {t('evaporationLimit.addLimit', 'Add Limit')}
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={limits}
              columns={limitColumns}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Box>
        </Paper>
      )}

      {/* Limit add/edit dialog */}
      <Dialog
        open={limitDialog.open}
        onClose={closeLimitDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={limitSaving}
      >
        <DialogTitle>
          {limitDialog.mode === 'create'
            ? t('evaporationLimit.addLimit', 'Add Limit')
            : t('evaporationLimit.editLimit', 'Edit Limit')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required
                label={t('evaporationLimit.purpose', 'Purpose')}
                value={limitDialog.form.purpose}
                onChange={setLimitField('purpose')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="limit-type-label" shrink>
                  {t('evaporationLimit.sign', 'Sign')}
                </InputLabel>
                <Select
                  labelId="limit-type-label"
                  label={t('evaporationLimit.sign', 'Sign')}
                  value={limitDialog.form.limits_type}
                  onChange={setLimitSelect('limits_type')}
                >
                  {LIMIT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                required
                type="number"
                label={t('evaporationLimit.rate', 'Rate (kg/m²/h)')}
                value={limitDialog.form.limits_rate}
                onChange={setLimitField('limits_rate')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="color"
                label={t('evaporationLimit.backgroundColor', 'Background Color')}
                value={limitDialog.form.background_color}
                onChange={setLimitField('background_color')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="color"
                label={t('evaporationLimit.textColor', 'Text Color')}
                value={limitDialog.form.text_color}
                onChange={setLimitField('text_color')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: limitDialog.form.background_color,
                  color: limitDialog.form.text_color,
                  textAlign: 'center',
                  border: '1px solid rgba(0,0,0,0.12)',
                }}
              >
                {t('evaporationLimit.preview', 'Preview')}: {limitDialog.form.limits_rate || '—'}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLimitDialog} disabled={limitSaving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveLimit}
            disabled={limitSaving}
            startIcon={limitSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          >
            {limitSaving
              ? t('common.saving', 'Saving...')
              : t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Limit delete confirmation */}
      <Dialog
        open={limitDeleteTarget !== null}
        onClose={() => !limitDeleting && setLimitDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={limitDeleting}
      >
        <DialogTitle>{t('evaporationLimit.deleteLimitTitle', 'Delete limit?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('evaporationLimit.deleteLimitBody', {
              defaultValue: 'Delete the "{{label}}" limit? This cannot be undone.',
              label: limitDeleteTarget?.purpose || limitDeleteTarget?.id,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDeleteTarget(null)} disabled={limitDeleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteLimit}
            disabled={limitDeleting}
            startIcon={limitDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {limitDeleting
              ? t('common.deleting', 'Deleting...')
              : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvaporationLimitForm;
